/**
 * Card — ONE bordered rectangle. Inside it: a body on its own, or a body with a header
 * above and/or a footer below, stacked with no gap. Header and footer carry no border of
 * their own, but a touch of chrome tint (`--argus-card-chrome`: a shade darker than the
 * body in light mode, a shade lighter in dark mode — PO finding 2026-10-05 #4) so they read
 * as the card's frame. When a header is present the body gets a top border + top radius;
 * when a footer is present, a bottom border + bottom radius. Only the body can take a
 * different background (`tone`). Nothing is inset.
 *
 * `Card` looks at its direct children to know whether a header/footer is present and
 * tells the body through context, so the body's edges are right without a `:has()`.
 */
import {
  Children,
  createContext,
  isValidElement,
  useContext,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

export type CardTone = 'card' | 'subtle' | 'accent' | 'transparent';

interface CardShape {
  hasHeader: boolean;
  hasFooter: boolean;
}

const CardContext = createContext<CardShape>({ hasHeader: false, hasFooter: false });

export interface CardProps extends HTMLAttributes<HTMLElement> {
  as?: 'section' | 'div' | 'article' | undefined;
  clickable?: boolean | undefined;
}

export function Card({
  as: Tag = 'section',
  clickable = false,
  className,
  children,
  ...rest
}: CardProps): JSX.Element {
  let hasHeader = false;
  let hasFooter = false;
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === CardHeader) hasHeader = true;
    if (child.type === CardFooter) hasFooter = true;
  });
  return (
    <CardContext.Provider value={{ hasHeader, hasFooter }}>
      <Tag
        className={['argus-card', clickable ? 'argus-card--clickable' : '', className ?? '']
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        {children}
      </Tag>
    </CardContext.Provider>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  id,
  headingLevel = 2,
}: {
  title: ReactNode;
  description?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  id?: string | undefined;
  /**
   * The heading element (F-18). Default `h2`; a card nested under a screen-level `h2` (or a
   * card inside a card) passes 3 or 4 so the outline stays a tree. Styling is identical.
   */
  headingLevel?: 2 | 3 | 4 | undefined;
}): JSX.Element {
  const Heading = `h${headingLevel}` as const;
  return (
    <header className="argus-card__header">
      <div className="grow">
        <Heading id={id} className="argus-card__title">
          {title}
        </Heading>
        {description ? <p className="argus-card__description">{description}</p> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </header>
  );
}

export function CardBody({
  flush = false,
  tone,
  className,
  style,
  children,
}: {
  /** No padding — for tables and lists that run edge to edge. */
  flush?: boolean | undefined;
  /** Background of the body; the only region that may differ from the card colour. */
  tone?: CardTone | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
  children: ReactNode;
}): JSX.Element {
  const { hasHeader, hasFooter } = useContext(CardContext);
  return (
    <div
      className={[
        'argus-card__body',
        flush ? 'argus-card__body--flush' : '',
        hasHeader ? 'argus-card__body--after-header' : '',
        hasFooter ? 'argus-card__body--before-footer' : '',
        tone ? `argus-card__body--tone-${tone}` : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
    >
      {children}
    </div>
  );
}

export function CardFooter({ children }: { children: ReactNode }): JSX.Element {
  return <footer className="argus-card__footer">{children}</footer>;
}

/** A KPI tile (label / value / hint) — the §13 loading state is the caller's Skeleton. */
export function Kpi({
  label,
  value,
  hint,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode | undefined;
}): JSX.Element {
  return (
    <div className="argus-kpi">
      <span className="argus-kpi__label">{label}</span>
      <span className="argus-kpi__value">{value}</span>
      {hint ? <span className="argus-kpi__hint">{hint}</span> : null}
    </div>
  );
}
